<template>
  <div class="content evidence-page">
    <div class="md-layout">
      <div class="md-layout-item md-size-100">
        <md-card>
          <md-card-header data-background-color="blue">
            <div class="header-flex">
              <div class="header-text">
                <h4 class="title">Listado de evidencias - {{ filteredDocs.length }}</h4>
                <p class="category">
                  Consulta las evidencias y su estado de valoración jurídica.
                </p>
              </div>
              <div class="header-actions" v-if="isPresident">
                <md-switch v-model="showOnlyFinalized" class="md-primary white-switch">Solo Finalizadas</md-switch>
              </div>
            </div>
          </md-card-header>

          <md-card-content>

            <p v-if="loading">Cargando evidencias...</p>
            <p v-else-if="error" class="text-danger">{{ error }}</p>
            <p v-else-if="!filteredDocs.length">
              No se encontraron evidencias registradas para esta comisión.
            </p>

            <div v-else class="table-wrap">
              <table class="institutional-table">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="docItem in filteredDocs" :key="docItem.id">
                    <td>
                      <div class="doc-name">{{ docItem.name || "Sin nombre" }}</div>
                      <div class="doc-kind">{{ docItem?.uploader?.name|| "Sin autor"}}</div>
                    </td>
                    <td>
                      <span class="status-pill" :class="statusTone(docItem)">
                        {{ statusLabel(docItem) }}
                      </span>
                    </td>
                    <td>{{ formatValue(docItem.createdAt) }}</td>
                    <td>
                      <md-button 
                        v-if="canReview(docItem)" 
                        class="md-dense md-primary review-button" 
                        @click="reviewDocument(docItem)"
                      >
                        Revisión
                      </md-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/fb";
import { saveEvidenceSelection } from "@/evidenceReviewContext";
const DEFAULT_COHORT = "cohort2026";
const DEFAULT_COMMISSION_ID = "NQfEjEI8jOHM26Aj9HvQ";

export default {
  name: "CaseEvidencePage",
  data() {
    return {
      loading: false,
      error: "",
      evidenceDocs: [],
      showOnlyFinalized: false,
    };
  },
  computed: {
    authContext() {
      return this.$delegateStore.state.authContext || {};
    },
    isPresident() {
      return this.authContext.role === "president";
    },
    filteredDocs() {
      const finalizedFilter = (docItem) => {
        const status = this.statusLabel(docItem).toLowerCase();
        return status.includes("aprob") || status.includes("comprob");
      };

      if (this.isPresident) {
        return this.showOnlyFinalized ? this.evidenceDocs.filter(finalizedFilter) : this.evidenceDocs;
      }

      // Delegado: Ve sus propias evidencias SIEMPRE.
      // Solo ve las de otros si son públicas o se compartieron con él.
      return this.evidenceDocs.filter(docItem => this.canSee(docItem));
    },
    currentDelegationId() {
      return (
        this.$delegateStore.state.delegateData &&
        this.$delegateStore.state.delegateData.delegation &&
        this.$delegateStore.state.delegateData.delegation.id
      );
    },
  },
  created() {
    this.loadEvidenceDocs();
  },
  methods: {
    friendlyType(type) {
      if (!type) {
        return "Archivo";
      }

      const normalizedType = String(type).toLowerCase();
      if (normalizedType.includes("image")) {
        return "Imagen";
      }

      if (normalizedType.includes("pdf")) {
        return "PDF";
      }

      if (normalizedType.includes("video")) {
        return "Video";
      }

      if (normalizedType.includes("audio")) {
        return "Audio";
      }

      return "Archivo";
    },
    formatValue(value) {
      if (!value) {
        return "-";
      }

      if (value.seconds !== undefined && value.nanoseconds !== undefined) {
        return new Date(value.seconds * 1000).toLocaleString();
      }

      if (typeof value === "string") {
        return value;
      }

      if (value.toDate && typeof value.toDate === "function") {
        return value.toDate().toLocaleString();
      }

      return "-";
    },
    statusLabel(docItem) {
      return (
        docItem.status ||
        docItem.reviewStatus ||
        docItem.state ||
        "Pendiente"
      );
    },
    statusTone(docItem) {
      const status = String(this.statusLabel(docItem)).toLowerCase();

      if (status.includes("aprob")) {
        return "status-approved";
      }

      if (status.includes("rech")) {
        return "status-rejected";
      }

      if (status.includes("revis")) {
        return "status-review";
      }

      if (status.includes("comprob")) {
        return "status-unprovable";
      }

      return "status-pending";
    },
    canSee(docItem) {
      if (this.isPresident) return true;
      const isOwner = docItem.uploader?.id === this.currentDelegationId;
      const isPublic = !!docItem.sharedWithDelegates;
      const isSpecificallyShared = Array.isArray(docItem.sharedDelegations) && docItem.sharedDelegations.includes(this.currentDelegationId);
      
      return isOwner || isPublic || isSpecificallyShared;
    },
    canReview(docItem) {
      if (this.isPresident) return true;
      // Para delegados, el botón de revisión (ver notas) requiere que se haya compartido 
      // (públicamente o a su delegación) o que sea propia.
      return this.canSee(docItem);
    },
    isFinalized(docItem) {
      const status = this.statusLabel(docItem).toLowerCase();
      return status.includes("aprob") || status.includes("comprob");
    },
    labelsText(docItem) {
      if (!docItem.labels) {
        return "Sin etiquetas";
      }

      if (Array.isArray(docItem.labels)) {
        return docItem.labels.join(", ");
      }

      return String(docItem.labels);
    },
    async loadEvidenceDocs() {
      this.loading = true;
      this.error = "";
      this.evidenceDocs = [];

      try {
        if (!this.currentDelegationId) {
          await this.$loadDelegateProfile();
        }

        const cohort = this.authContext.cohort || DEFAULT_COHORT;
        const commissionId = this.authContext.commissionId || DEFAULT_COMMISSION_ID;

        const mediaRef = collection(
          db,
          "commissions",
          cohort,
          "commissions",
          commissionId,
          "media"
        );

        const evidenceQuery = query(mediaRef, where("labels", "array-contains", "evidencia"));
        const snapshot = await getDocs(evidenceQuery);

        this.evidenceDocs = snapshot.docs
          .map((mediaDoc) => ({ id: mediaDoc.id, ...mediaDoc.data() }))
          .sort((left, right) => {
            const leftDate = left.updatedAt?.seconds || left.createdAt?.seconds || 0;
            const rightDate = right.updatedAt?.seconds || right.createdAt?.seconds || 0;
            return rightDate - leftDate;
          });
      } catch (error) {
        this.error =
          "No se pudieron cargar las evidencias. Verifica la ruta de Firestore y las reglas.";
      } finally {
        this.loading = false;
      }
    },
    reviewDocument(docItem) {
      saveEvidenceSelection(docItem);

      this.$router.push({
        path: this.isPresident ? "/evidence-review/president" : "/evidence-review",
        query: { evidenceId: docItem.id },
      });
    },
  },
};
</script>

<style scoped>
.evidence-page {
  padding-bottom: 24px;
}

.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 8px 4px 0;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5f6b7a;
}

.page-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  color: #1f2937;
}

.page-description {
  margin: 8px 0 0;
  max-width: 720px;
  color: #64748b;
}

.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 8px 4px 0;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5f6b7a;
}

.page-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  color: #1f2937;
}

.page-description {
  margin: 8px 0 0;
  max-width: 720px;
  color: #64748b;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-item {
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
}

.summary-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.institutional-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.institutional-table th,
.institutional-table td {
  padding: 14px 12px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: middle;
}

.institutional-table th {
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.doc-name {
  font-weight: 600;
  color: #0f172a;
}

.doc-kind {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}

.status-pending {
  background: #f8fafc;
  color: #475569;
  border-color: #cbd5e1;
}

.status-review {
  background: #f1f5f9;
  color: #23233a;
  border-color: #cbd5e1;
}

.status-approved {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.status-rejected {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.status-unprovable {
  background: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.white-switch /deep/ .md-switch-label {
  color: white !important;
  font-weight: 500;
  margin-left: 8px;
}

.review-button {
  margin: 0;
}

.md-card-actions {
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
